/** Pure geometry for the trend chart. Values may be null (no data that day);
 *  a null starts a new subpath rather than drawing a line across the gap. */

export interface Pt { i: number; v: number | null }

export function xForDay(i: number, n: number, w: number): number {
  return n <= 1 ? w / 2 : (i * w) / (n - 1)
}

/** A marker's x position, kept inside the plot. */
export function markerX(dayIndex: number, n: number, w: number): number {
  return xForDay(Math.min(Math.max(dayIndex, 0), Math.max(n - 1, 0)), n, w)
}

const yFor = (v: number, h: number, max: number) => h - (Math.min(Math.max(v, 0), max) / max) * h
const r1 = (x: number) => Math.round(x * 10) / 10

/** A line through the points, with a gap at every null. */
export function linePath(points: Pt[], n: number, w: number, h: number, max: number): string {
  if (max <= 0) return ''
  let d = ''
  let pen = false
  let prev = -2
  for (const p of points) {
    if (p.v === null) { pen = false; continue }
    const cmd = pen && p.i === prev + 1 ? 'L' : 'M'
    d += `${cmd}${r1(xForDay(p.i, n, w))},${r1(yFor(p.v, h, max))}`
    pen = true
    prev = p.i
  }
  return d
}

/** The low–high band as filled areas, one closed subpath per run of days that have both ends. */
export function bandPath(low: (number | null)[], high: (number | null)[], w: number, h: number, max: number): string {
  const n = Math.max(low.length, high.length)
  if (max <= 0) return ''
  const runs: number[][] = []
  let run: number[] = []
  for (let i = 0; i < n; i++) {
    if (low[i] === null || low[i] === undefined || high[i] === null || high[i] === undefined) {
      if (run.length) runs.push(run)
      run = []
    } else run.push(i)
  }
  if (run.length) runs.push(run)
  return runs
    .map((r) => {
      const top = r.map((i, k) => `${k === 0 ? 'M' : 'L'}${r1(xForDay(i, n, w))},${r1(yFor(high[i] as number, h, max))}`)
      const bottom = [...r].reverse().map((i) => `L${r1(xForDay(i, n, w))},${r1(yFor(low[i] as number, h, max))}`)
      return `${top.join('')}${bottom.join('')}Z`
    })
    .join('')
}

/** Split a series into the finished part (solid) and the unfinished tail
 *  (dotted). `incompleteFrom` is the index of the first unfinished day; the
 *  dotted run starts at the last finished day so the line stays joined. */
export function splitIncomplete(values: (number | null)[], incompleteFrom: number | undefined): { solid: Pt[]; dotted: Pt[] } {
  const all: Pt[] = values.map((v, i) => ({ i, v }))
  if (incompleteFrom === undefined || incompleteFrom >= values.length) return { solid: all, dotted: [] }
  const from = Math.max(incompleteFrom, 0)
  return { solid: all.slice(0, from), dotted: all.slice(Math.max(from - 1, 0)) }
}
